/**
 * src/mcp/hooks/useMCP.js
 *
 * Relocated from src/api/useMCP.js — grouped with the rest of the MCP chat
 * feature (folder-structure cleanup). Only import path changed: the
 * sibling API client now lives at ../api/mcp instead of ./mcp.
 *
 * React hooks for MCP functionality
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import mcpApi from '../api/mcp';

/**
 * Hook for managing MCP server connections and tools
 */
export function useMCPClient() {
  const [servers, setServers] = useState([]);
  const [tools, setTools] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [serversRes, toolsRes] = await Promise.all([
        mcpApi.getServers(),
        mcpApi.getTools(),
      ]);
      setServers(serversRes.servers || []);
      setTools(toolsRes.tools || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connectStdio = useCallback(async (name, command, env = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await mcpApi.connectStdioServer(name, command, env);
      await refresh();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refresh]);

  const connectSSE = useCallback(async (name, url, headers = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await mcpApi.connectSSEServer(name, url, headers);
      await refresh();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refresh]);

  const disconnect = useCallback(async (name) => {
    setIsLoading(true);
    try {
      await mcpApi.disconnectServer(name);
      await refresh();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refresh]);

  const disconnectAll = useCallback(async () => {
    setIsLoading(true);
    try {
      await mcpApi.disconnectAllServers();
      await refresh();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refresh]);

  const invokeTool = useCallback(async (server, tool, args) => {
    return mcpApi.invokeTool(server, tool, args);
  }, []);

  return {
    servers,
    tools,
    isLoading,
    error,
    connectStdio,
    connectSSE,
    disconnect,
    disconnectAll,
    invokeTool,
    refresh,
  };
}

/**
 * Hook for MCP agent chat
 */
export function useMCPAgent() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    mcpApi.getModels()
      .then(data => {
        setModels(data.models || []);
        setCurrentModel(data.current || '');
      })
      .catch(() => {});
  }, []);

  const sendQuery = useCallback(async (query, options = {}) => {
    const userMsg = {
      role: 'user',
      content: query,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);

    try {
      const data = await mcpApi.query(query, options);
      const agentMsg = {
        role: 'assistant',
        content: data.response,
        toolCalls: data.tool_calls,
        model: data.model,
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, agentMsg]);
      return data;
    } catch (err) {
      setError(err.message);
      const errorMsg = {
        role: 'assistant',
        content: `Error: ${err.message}`,
        isError: true,
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, errorMsg]);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearHistory = useCallback(async () => {
    try {
      await mcpApi.clearHistory();
      setMessages([]);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const changeModel = useCallback(async (model) => {
    try {
      await mcpApi.setModel(model);
      setCurrentModel(model);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    messages,
    isLoading,
    models,
    currentModel,
    error,
    sendQuery,
    clearHistory,
    changeModel,
    setMessages,
  };
}

/**
 * Hook for WebSocket streaming chat
 */
export function useMCPWebSocket() {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const wsRef = useRef(null);
  const currentResponseRef = useRef('');

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = mcpApi.createWebSocket();

    ws.onopen = () => setIsConnected(true);

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'chunk':
          currentResponseRef.current += data.data;
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.isStreaming) {
              last.content = currentResponseRef.current;
            }
            return [...updated];
          });
          break;

        case 'tool_calls':
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last) last.toolCalls = data.data;
            return [...updated];
          });
          break;

        case 'done':
          setIsStreaming(false);
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last) last.isStreaming = false;
            return [...updated];
          });
          currentResponseRef.current = '';
          break;

        case 'error':
          setIsStreaming(false);
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: `Error: ${data.data?.message}`, isError: true },
          ]);
          break;

        case 'cleared':
          setMessages([]);
          break;

        case 'pong':
          break;
      }
    };

    ws.onclose = () => setIsConnected(false);
    ws.onerror = () => setIsConnected(false);

    wsRef.current = ws;
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  const sendQuery = useCallback((query) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    setMessages(prev => [
      ...prev,
      { role: 'user', content: query, timestamp: Date.now() },
      { role: 'assistant', content: '', isStreaming: true, timestamp: Date.now() },
    ]);

    setIsStreaming(true);
    currentResponseRef.current = '';

    wsRef.current.send(JSON.stringify({ type: 'query', query }));
  }, []);

  const clearHistory = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'clear' }));
    }
    setMessages([]);
  }, []);

  useEffect(() => {
    return () => wsRef.current?.close();
  }, []);

  return {
    messages,
    isConnected,
    isStreaming,
    connect,
    disconnect,
    sendQuery,
    clearHistory,
  };
}

/**
 * Hook for health monitoring
 */
export function useMCPHealth() {
  const [health, setHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await mcpApi.getHealth();
      setHealth(data);
    } catch (err) {
      setHealth({ status: 'error', error: err.message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { health, isLoading, refresh };
}
